import React, { useState } from 'react';

interface ZoneBucket {
  zone: number;
  name: string;
  minHR: number;
  maxHR: number;
  timeInZone: number; // seconds
  percentageOfTime: number;
}

interface PaceBucket {
  bucket: string; // e.g. "Easy", "Tempo", "Threshold"
  minSecPerKm: number;
  maxSecPerKm: number;
  timeInBucket: number;
  percentageOfTime: number;
}

interface PowerBucket {
  zone: number;
  name: string; // Coggan zone name
  minWatts: number;
  maxWatts: number;
  timeInZone: number;
  percentageOfTime: number;
}

interface HRZoneData {
  avgHeartRate?: number;
  maxHeartRate?: number;
  hrZones?: ZoneBucket[];
  samples?: Array<{
    heartRate?: number;
    timestamp: string;
  }>;
  // 🆕 Giggly-Baking-Marshmallow Track 5.2: medium-aware distributions.
  // Already present in unified_athletic_data JSONB columns:
  //   pace_zones_distribution / power_zones_distribution
  // Surfaced here for conditional rendering when intensity_language ≠ zone_hr.
  paceZones?: PaceBucket[];
  powerZones?: PowerBucket[];
  avgPaceSecPerKm?: number;
  avgPowerWatts?: number;
  // RPE collected post-workout (migration 1564); when present, render
  // RPE distribution chart for rpe-prescribed users.
  sessionRPEValue?: number; // 1.0-10.0
  sessionRPESource?: 'user_post_workout' | 'chat_extracted' | 'inferred_from_hr' | 'imported';
}

// 🆕 Giggly-Baking-Marshmallow Track 5.2: intensity_language enum
// mirrors db migration 1471's CHECK constraint exactly.
type IntensityLanguage = 'zone_hr' | 'pace' | 'rpe' | 'power' | 'load_tss' | 'hybrid';

interface ExpandableHeartRateZonesProps {
  data: HRZoneData;
  plannedZones?: Record<string, any> | null;
  // 🆕 Giggly-Baking-Marshmallow Track 5.2: intensity_language from
  // AnchorPayload. When undefined (back-compat for pre-sprint callers),
  // component renders legacy HR-zone view exactly as today.
  intensityLanguage?: IntensityLanguage | null;
  // Optional callback for RPE input (rpe-prescribed users post-workout).
  onSessionRPESubmit?: (rpe: number) => void;
}

export const ExpandableHeartRateZones: React.FC<ExpandableHeartRateZonesProps> = ({
  data,
  plannedZones,
  intensityLanguage,
  onSessionRPESubmit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingRPE, setPendingRPE] = useState<number | null>(null);

  // Branch by intensity_language. Undefined/null falls through to legacy
  // HR-zone render (back-compat preserved EXACTLY).
  const lang: IntensityLanguage = intensityLanguage || 'zone_hr';

  // Per-medium data-presence gates.
  const hasHRData = data.avgHeartRate || data.maxHeartRate || (data.hrZones && data.hrZones.length > 0);
  const hasPaceData = data.avgPaceSecPerKm || (data.paceZones && data.paceZones.length > 0);
  const hasPowerData = data.avgPowerWatts || (data.powerZones && data.powerZones.length > 0);
  const hasRPEData = data.sessionRPEValue !== undefined && data.sessionRPEValue !== null;

  // Empty-state gating per medium.
  if (lang === 'zone_hr' && !hasHRData) return null;
  if (lang === 'pace' && !hasPaceData && !hasHRData) return null;  // fallback: show HR if no pace
  if (lang === 'power' && !hasPowerData && !hasHRData) return null;
  if (lang === 'rpe' && !hasRPEData && !hasHRData && !onSessionRPESubmit) return null;
  if (lang === 'hybrid' && !hasHRData && !hasPaceData) return null;
  if (lang === 'load_tss' && !hasHRData && !hasPaceData) return null;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getZoneColor = (zone: number): string => {
    const colors = [
      '#9CA3AF', // Zone 1 - Recovery (gray)
      '#3B82F6', // Zone 2 - Aerobic (blue)
      '#10B981', // Zone 3 - Tempo (green)
      '#F59E0B', // Zone 4 - Threshold (orange)
      '#EF4444', // Zone 5 - Max (red)
    ];
    return colors[zone - 1] || '#9CA3AF';
  };

  // 🆕 Giggly-Baking-Marshmallow Track 5.2: section title varies by medium.
  // This is the user-visible naming the user explicitly flagged.
  const sectionTitle = (() => {
    switch (lang) {
      case 'pace':
        return { emoji: '🏃', label: 'Pace Distribution' };
      case 'power':
        return { emoji: '⚡', label: 'Power Distribution' };
      case 'rpe':
        return { emoji: '💪', label: 'Effort & Recovery' };
      case 'hybrid':
        return { emoji: '❤️', label: 'Heart Rate Zones + Pace Distribution' };
      case 'load_tss':
        return { emoji: '📊', label: 'Intensity Distribution' };
      case 'zone_hr':
      default:
        return { emoji: '❤️', label: 'Heart Rate Zones' };
    }
  })();

  const headerAvg = (() => {
    if (lang === 'pace' && data.avgPaceSecPerKm) {
      const mins = Math.floor(data.avgPaceSecPerKm / 60);
      const secs = data.avgPaceSecPerKm % 60;
      return `Avg: ${mins}:${secs.toString().padStart(2, '0')}/km`;
    }
    if (lang === 'power' && data.avgPowerWatts) {
      return `Avg: ${Math.round(data.avgPowerWatts)}W`;
    }
    if (lang === 'rpe' && data.sessionRPEValue) {
      return `RPE: ${data.sessionRPEValue.toFixed(1)}/10`;
    }
    if (data.avgHeartRate) {
      return `Avg: ${data.avgHeartRate} bpm`;
    }
    return null;
  })();

  return (
    <div className="border-t border-gray-700 pt-3 mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">{sectionTitle.emoji} {sectionTitle.label}</span>
          {headerAvg && (
            <span className="text-xs text-gray-400">{headerAvg}</span>
          )}
        </div>
        <span className="text-gray-400 text-sm">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {/* 🆕 Giggly-Baking-Marshmallow Track 5.2: medium-specific
              summary stats. zone_hr/hybrid keep existing HR cards; other
              mediums show their native units as primary. */}
          {(lang === 'zone_hr' || lang === 'hybrid') && (
            <div className="grid grid-cols-2 gap-3">
              {data.avgHeartRate && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Average HR</div>
                  <div className="text-lg font-semibold text-white">{data.avgHeartRate} bpm</div>
                </div>
              )}
              {data.maxHeartRate && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Max HR</div>
                  <div className="text-lg font-semibold text-white">{data.maxHeartRate} bpm</div>
                </div>
              )}
            </div>
          )}

          {lang === 'pace' && (
            <div className="grid grid-cols-2 gap-3">
              {data.avgPaceSecPerKm && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Average Pace</div>
                  <div className="text-lg font-semibold text-white">
                    {Math.floor(data.avgPaceSecPerKm / 60)}:{(data.avgPaceSecPerKm % 60).toString().padStart(2, '0')}/km
                  </div>
                </div>
              )}
              {data.avgHeartRate && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">HR (recorded)</div>
                  <div className="text-lg font-semibold text-white">{data.avgHeartRate} bpm</div>
                </div>
              )}
            </div>
          )}

          {lang === 'power' && (
            <div className="grid grid-cols-2 gap-3">
              {data.avgPowerWatts && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Average Power</div>
                  <div className="text-lg font-semibold text-white">{Math.round(data.avgPowerWatts)}W</div>
                </div>
              )}
              {data.avgHeartRate && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">HR (recorded)</div>
                  <div className="text-lg font-semibold text-white">{data.avgHeartRate} bpm</div>
                </div>
              )}
            </div>
          )}

          {lang === 'rpe' && (
            <div className="bg-gray-800 rounded-lg p-3">
              {data.sessionRPEValue !== undefined && data.sessionRPEValue !== null ? (
                <>
                  <div className="text-xs text-gray-400 mb-1">Session RPE</div>
                  <div className="text-lg font-semibold text-white">{data.sessionRPEValue.toFixed(1)}/10</div>
                  {data.sessionRPESource && (
                    <div className="text-xs text-gray-500 mt-1">
                      {data.sessionRPESource === 'user_post_workout' && 'You logged this RPE'}
                      {data.sessionRPESource === 'chat_extracted' && 'Extracted from chat'}
                      {data.sessionRPESource === 'inferred_from_hr' && 'Inferred from HR (approximate)'}
                      {data.sessionRPESource === 'imported' && 'Imported from connected app'}
                    </div>
                  )}
                </>
              ) : onSessionRPESubmit ? (
                <div className="space-y-2">
                  <div className="text-xs text-gray-400">How did that feel? (RPE 1-10)</div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <button
                        key={n}
                        onClick={() => setPendingRPE(n)}
                        className={`w-10 h-10 rounded-full text-sm font-semibold ${
                          pendingRPE === n
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        type="button"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {pendingRPE !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pendingRPE !== null && onSessionRPESubmit) {
                          onSessionRPESubmit(pendingRPE);
                          setPendingRPE(null);
                        }
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded text-sm"
                    >
                      Log RPE {pendingRPE}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">No RPE logged for this session.</div>
              )}
            </div>
          )}

          {/* 🆕 Giggly-Baking-Marshmallow Track 5.2: Pace distribution
              for pace-prescribed users. Reads paceZones from
              unified_athletic_data.pace_zones_distribution. */}
          {(lang === 'pace' || lang === 'hybrid') && data.paceZones && data.paceZones.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium mb-2">Time in Each Pace Band</div>
              {data.paceZones.map((bucket, idx) => (
                <div key={bucket.bucket} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-300">
                      {bucket.bucket} (
                      {Math.floor(bucket.minSecPerKm / 60)}:{(bucket.minSecPerKm % 60).toString().padStart(2, '0')}
                      –
                      {Math.floor(bucket.maxSecPerKm / 60)}:{(bucket.maxSecPerKm % 60).toString().padStart(2, '0')}/km)
                    </span>
                    <span className="text-gray-400">
                      {formatTime(bucket.timeInBucket)} ({bucket.percentageOfTime.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${bucket.percentageOfTime}%`,
                        backgroundColor: ['#60A5FA', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'][idx % 5],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 🆕 Giggly-Baking-Marshmallow Track 5.2: Power distribution
              for Stryd-using power-prescribed users. */}
          {lang === 'power' && data.powerZones && data.powerZones.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium mb-2">Time in Each Power Zone</div>
              {data.powerZones.map((zone, idx) => (
                <div key={zone.zone} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-300">
                      Z{zone.zone}: {zone.name} ({zone.minWatts}-{zone.maxWatts}W)
                    </span>
                    <span className="text-gray-400">
                      {formatTime(zone.timeInZone)} ({zone.percentageOfTime.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${zone.percentageOfTime}%`,
                        backgroundColor: ['#A78BFA', '#8B5CF6', '#EC4899', '#F43F5E', '#EF4444', '#DC2626', '#991B1B'][Math.min(idx, 6)],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Existing HR Zone Distribution (zone_hr + hybrid + back-compat) */}
          {(lang === 'zone_hr' || lang === 'hybrid') && data.hrZones && data.hrZones.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium mb-2">Time in Each Zone</div>
              {data.hrZones.map((zone) => {
                const plannedEntry = plannedZones?.[`zone${zone.zone}`];
                const plannedPct = plannedEntry
                  ? (plannedEntry.targetPercent ?? plannedEntry.target_percent ?? null)
                  : null;
                return (
                  <div key={zone.zone} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-300">
                        Zone {zone.zone}: {zone.name} ({zone.minHR}-{zone.maxHR} bpm)
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400">
                          {formatTime(zone.timeInZone)} ({zone.percentageOfTime.toFixed(1)}%)
                        </span>
                        {plannedPct != null && (
                          <span className="text-gray-500 ml-2 text-xs italic">
                            planned {plannedPct}%
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Actual progress bar */}
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${zone.percentageOfTime}%`,
                          backgroundColor: getZoneColor(zone.zone)
                        }}
                      />
                    </div>
                    {/* Planned overlay bar */}
                    {plannedPct != null && (
                      <div className="w-full bg-gray-700 rounded-full h-1.5 -mt-0.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: `${plannedPct}%`,
                            backgroundColor: getZoneColor(zone.zone),
                            opacity: 0.35,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* HR Chart from samples (if available) */}
          {data.samples && data.samples.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-2">Heart Rate Over Time</div>
              <div className="text-xs text-gray-500">
                📊 {data.samples.filter(s => s.heartRate).length} HR data points
              </div>
              {/* TODO: Add Chart.js or canvas-based HR chart visualization */}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

